import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useEffect, useState, Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { USER } from '../../../utils/constants';
import { INFLUENCERS_ENDPOINTS } from '../../../config/api';
import { useHistory, useParams } from "react-router-dom";
import { Col, Row, Spinner, Container } from 'react-bootstrap';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { setUser } from '../../../actions/auth.actions';
import RegistrationForm from '../../../shared/pages/UserRegistration/RegistrationForm'
import ResponsiveHeader from '../../../shared/components/ResponsiveHeader';

const CompleteRegistration = ({
  user = USER.INFLUENCERS
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { completeRegistrationToken } = useParams();
  const [state, setState] = useState({
    loading: true,
    error: false,
    user: {}
  });

  useEffect(() => {
    getPreRegisteredUser();
  }, [])

  const redirectToSignInWithBanner = (banner, bannerVariant = 'success') => {
    history.push({ pathname: '/signin', state: { banner, bannerVariant } });
  };

  // Handles the welcome-token-level failures (expired/invalid token, or a link that
  // was already used to complete registration) by redirecting to sign-in with a
  // banner. Returns true when it handled the error, so callers know not to also
  // render an inline/form-level error for the same response.
  const handleWelcomeAuthError = (code) => {
    if (code === 'already_registered') {
      redirectToSignInWithBanner(t('influencers.pages.user_registration.already_registered_banner'));
      return true;
    }

    if (code === 'invalid_token') {
      redirectToSignInWithBanner(t('influencers.pages.user_registration.invalid_token_banner'), 'danger');
      return true;
    }

    return false;
  };

  const getPreRegisteredUser = () => {
    setState({ ...state, loading: true });

    axios.get(
      INFLUENCERS_ENDPOINTS.ALLOW_PRE_REGISTERED_USER(completeRegistrationToken)
    ).then(({ data }) => {
      setState(
        {
          ...state,
          user: data,
          error: false,
          loading: false,
        }
      );
    }).catch((err) => {
      if (handleWelcomeAuthError(get(err, 'response.data.code'))) return;

      setState(
        {
          ...state,
          error: true,
          loading: false,
        }
      );
    });
  };

  const submitForm = (formikForm, values, formState, formSetState) => {
    formSetState({ ...formState, loading: true });

    axios.put(
      INFLUENCERS_ENDPOINTS.USERS.PUT(state.user.id),
      {
        welcome_token: completeRegistrationToken,
        user: {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          password: values.password,
          password_confirmation: values.password_confirmation,
          tos_last_accepted: values.tos_last_accepted,
          privacy_policy_last_accepted: values.privacy_policy_last_accepted,
          newsletter_consent: values.newsletter_consent,
          dob: values.dob,
          gender: values.gender,
          inst_username: values.ig_handle,
          tikt_username: values.tt_handle,
          yout_url: values.yt_handle,
          link_username: values.link_handle,
          address_1: values.address_1,
          address_2: values.address_2,
          city: values.city,
          state: values.state,
          zip: values.zip,
          country: values.country
        }
      }
    ).then(({ data }) => {
      formSetState({ ...formState, loading: false });
      dispatch(setUser(data.user));
      history.push('/sign-up/step-3');
    }).catch((err) => {
      if (handleWelcomeAuthError(get(err, 'response.data.code'))) return;

      const errors = get(err, 'response.data.errors');
      formikForm.setErrors(
        errors || { email: t('influencers.common.message_error') }
      );
      formSetState({ ...formState, loading: false });
    });
  };

  if (state.loading) {
    return(
      <Fragment>
        <Container>
          <Spinner animation="border" size="sm" />
        </Container>
      </Fragment>
    );
  }

  return(
    <Fragment>
      <Container>
        <ResponsiveHeader xs="h3" lg="h2">
          {t('influencers.pages.user_registration.header')}
        </ResponsiveHeader>
        <p className='mb-sm-4'>
          {t('influencers.pages.user_registration.sub_header')}
        </p>
        {
          state.error ?
          <div>ERRORS</div> :
          <Container>
            {
              <RegistrationForm
                onSubmitForm={submitForm}
                user={state.user}
                isPreRegisteredUser={true}
              />
            }
          </Container>
        }
      </Container>
    </Fragment>
  )
}

CompleteRegistration.propTypes = {
  user: PropTypes.string,
};

export default CompleteRegistration;
